// assets/turbo-first-touch.js - turbo.se FIRST-TOUCH attribution capture.
//
// THE BUG (2026-08-13): prod showed 84% of turbo_se leads (98/117 over 4 weeks)
// arriving with traffic_source falling back to 'turbo.se'/'direct' and no
// utm_source, and only 23.9% carrying a landing_page - even though the Worker
// (inskick-worker/src/crm.js, attributionFields()/resolveTrafficSource(), live
// since 2026-08-09) and the CRM (LeadIntakeController) both already validate,
// forward, and persist every one of these fields correctly. 18/19 turbo cpc
// leads DO carry utm_term, so the pipe downstream of the browser works fine
// when it receives real data - the loss is entirely upstream, client-side,
// before the lead ever reaches the Worker.
//
// ROOT CAUSE: the existing capture (turbo_attr, the IIFE in layout/theme.liquid
// that populates window.turboAttrFields()/window.turboChannel()) only WRITES to
// localStorage when window.turboMarketingAllowed() is true (Shopify Customer
// Privacy 'Marketing' consent - a deliberate GDPR choice; see the tracking-
// research corpus's T1 report, and it is the CORRECT Shopify taxonomy per its
// own docs - ad-click attribution is bucketed under 'Marketing', not
// 'Analytics'). Most visitors browse a page or two - or never interact with the
// consent banner at all - before they convert. By the time (if ever) marketing
// consent resolves true, the CURRENT page is no longer the landing page:
// document.referrer now reads turbo.se's own hostname (internal navigation),
// and the URL no longer carries the original utm_*/gclid. Whatever capture
// eventually fires records that already-corrupted touch - exactly the observed
// "traffic_source falls back to turbo.se/direct" + "no landing_page" pattern.
// CPC leads are the one case mostly unaffected: those visitors typically land
// AND convert on the SAME page via the persistent price-inquiry strip, so even
// a late consent grant still reads the still-correct URL.
//
// THE FIX mirrors the ALREADY-LIVE, ALREADY-WORKING pattern on the sibling
// meksta.se property (PageViewTracker.tsx / lib/attribution.ts, ~95% capture
// rate): capture referrer/landing_page/utm_* into a SEPARATE, first-write-wins
// snapshot, UNGATED by marketing consent, the moment the browser is first seen
// - exactly as meksta.se's own sessionStorage layer already does ahead of its
// own consent-gated durable cookie. This module does NOT touch turbo_attr, its
// 90-day cookie, or the marketing-consent gate at all: fbp/fbc/ga_client_id
// and the gclid/gbraid/wbraid/fbclid used for AD-PLATFORM conversion matching
// keep reading turbo_attr.last exactly as before, completely unchanged. Only
// the CRM-facing reporting fields (utm_source/medium/campaign/term/content,
// landing_page, and the classified traffic_source) move onto this new,
// reliable, first-touch snapshot - wired in by layout-theme-liquid.snippet.
// liquid (same directory), which extends window.turboAttrFields()/
// window.turboChannel() to read from it, with a same-behavior-as-today
// fallback if this file fails to load.
//
// STORAGE CONTRACT (binding, not just a forwarding-layer promise): every ungated
// snapshot this module writes to localStorage and to its cookie fallback - the
// first-touch one under `turbo_first_touch` and, since 2026-09-05, the
// last-touch one under `turbo_last_touch` (see the LAST TOUCH section) - is the
// same shape, and contains ONLY `ts`, `landing` (path), `referrer`,
// `utm_source`, `utm_medium`, `utm_campaign`, `utm_term`, `utm_content`, and a
// precomputed `channel` (one of the 6 classifyChannel() words). It NEVER contains an ad-platform
// identifier - no gclid, gbraid, wbraid, fbclid, fbp, or fbc - at the storage
// layer, not merely omitted from what firstTouchFields() forwards. The EDPB's
// Guidelines 2/2023 on the ePrivacy Directive's Article 5(3) regulate STORING
// information on a device independently of whether it is later read back or
// sent anywhere - the same basis the R4 memo used to gate the original
// turbo_attr capture - so an ungated store can only be defended by keeping
// click ids out of it entirely, not by keeping them in and merely declining to
// forward them. Where paid-channel classification needs a click id, it reads
// one LIVE from the current URL's query string at the single moment that URL
// legitimately carries it (the true landing view, inside buildTouch() below,
// captured while building the touch that is about to be persisted) and keeps
// only the classification WORD, never the id itself, in what gets written. A
// later page view's classification falls back to the safely-stored
// utm_source/utm_medium alone, exactly as `classifyChannel()` already does for
// those signals with no click id present.
//
// GDPR / ePrivacy note for the owner: this deliberately mirrors meksta.se's own
// shipped design, which does not gate its equivalent capture behind marketing
// consent for these same fields (utm_*, referrer, landing_page). That is a
// considered departure from turbo.se's current fully-marketing-gated posture,
// flagged explicitly here and in the PR description rather than decided
// unilaterally - worth an owner/legal sign-off alongside the live rollout.
//
// Deploy: this file is NOT yet pushed to the live Shopify theme (deployment is
// a separate, gated step - see the PR description). Once approved, push this
// file to the live theme's assets/turbo-first-touch.js via the Admin API
// (themeFilesUpsert, matching the T1/F1 sessions' own anchored-edit + ground-
// truth-reread methodology), then apply layout-theme-liquid.snippet.liquid's
// two anchored edits to the live layout/theme.liquid.

const STORAGE_KEY = 'turbo_first_touch';
const COOKIE_MAX_AGE_DAYS = 90;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
// Read LIVE from the URL, at build time only, to classify the true landing
// view's channel - never assigned onto the touch object that gets persisted.
// See the STORAGE CONTRACT note above.
const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid'];
// The LAST-touch snapshot lives under its own key, alongside (never inside) the
// first-write-wins first-touch one - see the LAST TOUCH section below.
const LAST_TOUCH_KEY = 'turbo_last_touch';
const LAST_TOUCH_MAX_AGE_MS = COOKIE_MAX_AGE_DAYS * 24 * 3600 * 1000;
// Every param whose mere PRESENCE marks a page view as a fresh acquisition
// entry. `pub` is Meta's placement param; the click ids are presence-only here
// exactly as they are in buildTouch() - their values are never stored.
const SIGNAL_PARAM_KEYS = UTM_KEYS.concat(['pub'], CLICK_ID_KEYS);

/**
 * Pure builder: given a location-like object ({pathname, search}) and a
 * document-like object ({referrer}), build the touch snapshot that gets
 * persisted. Reads no globals directly, so it is fully unit-testable without a
 * DOM.
 *
 * landing is PATH-ONLY (no query string) to match gsc_page_queries.page's
 * shape on the CRM side.
 *
 * The RETURNED object never carries gclid/gbraid/wbraid/fbclid: they are read
 * from `loc.search` here, live, purely to compute `channel` via
 * classifyChannel(), then discarded - the click-id values themselves are
 * never assigned onto `touch` and therefore never reach storage. This is the
 * one place in the module allowed to look at a click id at all; every other
 * function only ever sees the persisted, click-id-free shape.
 */
export function buildTouch(loc, doc) {
  const search = (loc && loc.search) || '';
  const params = new URLSearchParams(search);
  const pick = (key) => params.get(key) || '';
  const touch = {
    ts: Date.now(),
    landing: (loc && loc.pathname) || '/',
    referrer: (doc && doc.referrer) || '',
  };
  UTM_KEYS.forEach((key) => {
    touch[key] = pick(key);
  });
  // Meta's `pub={{publisher_platform}}` (facebook/instagram/audience_network):
  // a placement word, not a click id and not personal data, so it is stored
  // ungated next to the utms - the CRM keeps it as customers.ad_placement.
  touch.pub = pick('pub');
  touch.channel = classifyChannel({
    utm_medium: touch.utm_medium,
    utm_source: touch.utm_source,
    referrer: touch.referrer,
    fbclid: pick('fbclid'),
    gclid: pick('gclid'),
    gbraid: pick('gbraid'),
    wbraid: pick('wbraid'),
    msclkid: pick('msclkid'),
    ttclid: pick('ttclid'),
  });
  return touch;
}

/**
 * The 6-value channel vocabulary turboChannel() already emits (unchanged from
 * the live F1 precedence fix - gclid/gbraid/wbraid first, then a social_organic
 * utm_medium carve-out, then cpc/paid/ppc, then referrer-domain heuristics,
 * then a bare fbclid downgraded to organic_social, then referral/direct),
 * applied here to a FIRST-touch snapshot instead of turbo_attr's last-touch.
 */
export function classifyChannel(touch) {
  if (!touch) return 'direct';
  // Kept in lockstep with the CRM's ChannelResolver rule order (spec section 5):
  // msclkid means a Microsoft/Bing paid click, ttclid a TikTok paid click.
  if (touch.gclid || touch.gbraid || touch.wbraid || touch.msclkid) return 'paid_search';
  if (touch.ttclid) return 'paid_social';
  const medium = (touch.utm_medium || '').toLowerCase();
  if (medium === 'social_organic') return 'organic_social';
  // Live-only refinement (re-synced from the deployed theme, not previously in
  // git): a paid-shaped medium paired with a known social utm_source or a live
  // fbclid is paid_social even when the medium string itself has no "social"
  // substring (utm_medium=paid + utm_source=facebook, for example).
  const PAID_MEDIUM_RE = /^(paid|cpc|ppc|paid_social|paidsocial|social_paid)$/i;
  const SOCIAL_SOURCE_RE = /^(facebook|fb|instagram|ig|meta|l\.facebook\.com|lm\.facebook\.com|m\.facebook\.com|l\.instagram\.com)$/i;
  if (PAID_MEDIUM_RE.test(medium) && (SOCIAL_SOURCE_RE.test(touch.utm_source || '') || touch.fbclid)) {
    return 'paid_social';
  }
  if (medium.indexOf('cpc') > -1 || medium.indexOf('paid') > -1 || medium === 'ppc') {
    return medium.indexOf('social') > -1 ? 'paid_social' : 'paid_search';
  }
  const referrer = (touch.referrer || '').toLowerCase();
  const hasAny = (needles) => needles.some((needle) => referrer.indexOf(needle) > -1);
  if (hasAny(['google', 'bing', 'yahoo', 'duckduckgo'])) return 'organic_search';
  if (hasAny(['facebook', 'instagram', 't.co', 'linkedin', 'youtube'])) return 'organic_social';
  if (touch.fbclid) return 'organic_social';
  if (referrer) return 'referral';
  return 'direct';
}

function readCookie(name, doc) {
  try {
    const source = doc || (typeof document !== 'undefined' ? document : null);
    const raw = source && typeof source.cookie === 'string' ? source.cookie : '';
    const parts = raw.split(';');
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i].trim();
      if (part.indexOf(name + '=') === 0) {
        return decodeURIComponent(part.substring(name.length + 1));
      }
    }
  } catch (e) {
    // never let attribution break on a cookie read
  }
  return '';
}

function writeCookie(name, value, days, doc) {
  try {
    const target = doc || (typeof document !== 'undefined' ? document : null);
    if (!target) return;
    target.cookie = name + '=' + encodeURIComponent(value) + ';path=/;max-age=' + (days * 24 * 3600) + ';SameSite=Lax';
  } catch (e) {
    // never let attribution break a page load
  }
}

/**
 * Read the stored snapshot from a storage-like object (localStorage-shaped:
 * getItem), falling back to a same-name cookie - matches turbo_attr's own
 * localStorage-with-cookie-fallback convention exactly, so private-browsing /
 * storage-restricted visitors still get a working (session-lived) snapshot.
 */
export function readStoredTouch(storage, doc) {
  try {
    const raw = (storage && storage.getItem(STORAGE_KEY)) || readCookie(STORAGE_KEY, doc);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
}

/**
 * First-write-wins: capture the CURRENT touch only if no snapshot exists yet
 * for this browser. Never overwrites an existing snapshot, so a later
 * internal-navigation page view or a direct revisit can never clobber the true
 * first touch. Returns the snapshot now in storage (existing or newly
 * written). Deliberately ungated by marketing consent - see the file header.
 */
export function captureFirstTouchIfAbsent(storage, loc, doc) {
  const existing = readStoredTouch(storage, doc);
  if (existing) return existing;

  const touch = buildTouch(loc, doc);
  const json = JSON.stringify(touch);
  try {
    if (storage) storage.setItem(STORAGE_KEY, json);
  } catch (e) {
    // storage may be full/blocked - the cookie fallback below still lands
  }
  writeCookie(STORAGE_KEY, json, COOKIE_MAX_AGE_DAYS, doc);
  return touch;
}

/**
 * Everything the CRM's documented attribution contract wants out of the stored
 * FIRST touch, present-only.
 *
 * Emits ONLY `first_`-prefixed keys: first_utm_*, first_landing_page,
 * first_referrer, first_traffic_source, first_touch_at - the names the Worker
 * forwards as distinct CRM columns, so a first-touch value can never overwrite
 * the last-touch one beside it.
 *
 * Until 2026-09-05 this also emitted each utm key and the landing page under
 * its BARE name, because layout/theme.liquid filled the bare CRM columns from
 * `ft.utm_source || L.utm_source`. That was the other half of the attribution
 * gap this module was written to close: the CRM reads the bare `utm_*` /
 * `landing_page` / `traffic_source` columns as the LAST touch and the `first_*`
 * columns as the first touch, so a first-touch value sitting under a bare name
 * described the wrong visit - the acquisition entry, not the converting one.
 * The bare names now come from lastTouchFields() below, and nothing bare is
 * emitted here.
 *
 * Click ids stay out of here, exactly as before - see the STORAGE CONTRACT note
 * in this file's header. `first_traffic_source` is the precomputed channel WORD,
 * never the id that produced it.
 */
export function firstTouchFields(storage, doc) {
  const touch = readStoredTouch(storage, doc);
  if (!touch) return {};
  const out = {};
  UTM_KEYS.forEach((key) => {
    if (touch[key]) out['first_' + key] = touch[key];
  });
  if (touch.landing) out.first_landing_page = touch.landing;
  if (touch.referrer) out.first_referrer = touch.referrer;
  const channel = touch.channel || classifyChannel({utm_medium: touch.utm_medium, utm_source: touch.utm_source, referrer: touch.referrer});
  if (channel) out.first_traffic_source = channel;
  if (typeof touch.ts === 'number' && Number.isFinite(touch.ts)) {
    out.first_touch_at = new Date(touch.ts).toISOString();
  }
  return out;
}

/**
 * traffic_source: the classified channel of the TRUE first touch. Reads the
 * `channel` word precomputed once by buildTouch() at capture time (see the
 * STORAGE CONTRACT note above - this is how a later page view's read never
 * needs a click id, live or stored, to still report `paid_search`/
 * `paid_social` correctly). Falls back to classifying from the safely-stored
 * utm_medium/referrer alone for a snapshot written before this field existed
 * (an older captured session) - never from a click id, which by design is
 * never present in storage.
 */
export function firstTouchChannel(storage, doc) {
  const touch = readStoredTouch(storage, doc);
  if (!touch) return 'direct';
  if (touch.channel) return touch.channel;
  return classifyChannel({ utm_medium: touch.utm_medium, utm_source: touch.utm_source, referrer: touch.referrer });
}

// ---- LAST TOUCH (ungated, mirrors meksta.se's lib/firstTouch.ts) -------------
//
// THE SECOND HALF OF THE BUG (2026-09-05): everything above fixed the FIRST
// touch. The LAST touch - the visit that actually converted - was still read
// exclusively from `turbo_attr`, the consent-gated store in layout/theme.liquid,
// whose write path returns early unless window.turboMarketingAllowed(). A
// visitor who declines the banner and lands on ?utm_source=bing&utm_medium=cpc
// therefore sent NO utm at all: turbo_attr was never written, and the ungated
// first-touch snapshot only ever answers for the FIRST visit. That contradicts
// the owner's own first-party attribution policy (source capture is UNGATED,
// ad-platform ids are consent-gated) and it is the last remaining hole in it.
//
// So: an ungated last-touch snapshot, under its own key, holding exactly the
// same click-id-free shape buildTouch() already produces (ts, landing,
// referrer, utm_*, and the precomputed `channel` word). The STORAGE CONTRACT in
// this file's header applies to it verbatim - no gclid, gbraid, wbraid, fbclid,
// msclkid, ttclid or fbp/fbc ever reaches this store either. Click ids stay
// where they already are: in the consent-gated turbo_attr path, read by
// turboAttrFields() exactly as before.
//
// WHY THE CRM CARES WHICH IS WHICH: the CRM treats the bare `utm_*` /
// `landing_page` / `traffic_source` columns as the LAST touch and the `first_*`
// columns as the first touch. Before this section existed, layout/theme.liquid
// filled the bare columns from firstTouchFields(), so `traffic_source` on a
// returning visitor described the visit that first found turbo.se, not the one
// that submitted the form.

/** `new URL(url).hostname` minus a leading `www.`, lowercased. '' when unparseable/empty. Never throws. */
function hostOf(url) {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '').toLowerCase();
  } catch (e) {
    return '';
  }
}

/**
 * True only when this page view is a real acquisition entry:
 *
 *   - it carries any utm/pub/click-id param (SIGNAL_PARAM_KEYS), OR
 *   - its referrer is an EXTERNAL host that also differs from the host that
 *     produced the currently stored last touch.
 *
 * The host comparison is what keeps a single visit from blanking its own utm.
 * Shopify serves every page as a full document load, so document.referrer on
 * the second page of a visit is turbo.se itself (internal, filtered out here) -
 * but a link opened from the landing page in a new tab, or any theme surface
 * that preserves the original referrer, still reports the ORIGINAL external
 * host (google.com) with no params of its own. Without comparing against the
 * stored last touch's referrer HOST, that would look like a brand-new
 * acquisition entry and overwrite the utm the landing view captured.
 *
 * The comparison is HOST-level, not exact-string: a second hit from the same
 * referring site (a different google.com/search?q=... query, say) is the same
 * visit continuing, not a new source. A genuinely different external host with
 * no params of its own IS a fresh organic entry - see captureLastTouch.
 *
 * `existingLastReferrer` is the referrer of the currently stored last touch
 * (undefined/'' when there is none yet).
 */
export function isAcquisitionEntry(loc, doc, currentHost, existingLastReferrer) {
  const params = new URLSearchParams((loc && loc.search) || '');
  for (let i = 0; i < SIGNAL_PARAM_KEYS.length; i++) {
    if (params.get(SIGNAL_PARAM_KEYS[i])) return true;
  }
  const referrer = (doc && doc.referrer) || '';
  if (!referrer) return false;
  const refHost = hostOf(referrer);
  if (!refHost) return false;
  const here = String(currentHost || '').replace(/^www\./, '').toLowerCase();
  if (refHost === here) return false;
  if (!existingLastReferrer) return true;
  return refHost !== hostOf(existingLastReferrer);
}

/**
 * The stored last-touch snapshot, or null. Same localStorage-with-cookie-
 * fallback convention as the first touch, plus a manually tracked 90-day expiry
 * (localStorage has no native TTL, unlike the cookie's max-age) so a snapshot
 * older than the attribution window reads as absent instead of crediting a
 * campaign from three months ago.
 */
export function readStoredLastTouch(storage, doc, now) {
  try {
    const raw = (storage && storage.getItem(LAST_TOUCH_KEY)) || readCookie(LAST_TOUCH_KEY, doc);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const at = typeof now === 'number' && Number.isFinite(now) ? now : Date.now();
    if (typeof parsed.ts === 'number' && Number.isFinite(parsed.ts) && at - parsed.ts > LAST_TOUCH_MAX_AGE_MS) {
      return null;
    }
    return parsed;
  } catch (e) {
    return null;
  }
}

/**
 * Record the current page view as the last touch, but ONLY when it is a real
 * acquisition entry (or when nothing is stored yet, so every browser gets a
 * definitive snapshot on its very first page view). Otherwise the stored touch
 * is carried forward completely UNCHANGED - the stored object itself, never a
 * merge - so a same-visit page view can never lose the utm the landing view
 * captured.
 *
 * A genuinely new acquisition entry always writes a BRAND-NEW touch built only
 * from the current page view. Nothing is inherited from the previous one, even
 * when the new entry carries no params at all: arriving from a different
 * external host is a fresh organic touch from that source, not a continuation
 * of whatever campaign brought the visitor last time.
 *
 * Deliberately ungated by marketing consent, on exactly the same basis as the
 * first-touch capture above - and safe under it, because the persisted shape
 * carries no ad-platform identifier.
 */
export function captureLastTouch(storage, loc, doc, currentHost, now) {
  const existing = readStoredLastTouch(storage, doc, now);
  if (existing && !isAcquisitionEntry(loc, doc, currentHost, existing.referrer)) return existing;

  const touch = buildTouch(loc, doc);
  if (typeof now === 'number' && Number.isFinite(now)) touch.ts = now;
  const json = JSON.stringify(touch);
  try {
    if (storage) storage.setItem(LAST_TOUCH_KEY, json);
  } catch (e) {
    // storage may be full/blocked - the cookie fallback below still lands
  }
  writeCookie(LAST_TOUCH_KEY, json, COOKIE_MAX_AGE_DAYS, doc);
  return touch;
}

/**
 * The CRM's LAST-touch field set, present-only: the BARE `utm_*` keys plus
 * `landing_page`, `referrer` and `last_touch_at`. These are the names the CRM
 * reads as the converting visit, which is why they are deliberately unprefixed
 * and why firstTouchFields() no longer emits any of them.
 *
 * No click id and no `traffic_source` here - the channel is a separate call
 * (lastTouchChannel), matching firstTouchChannel's split.
 */
export function lastTouchFields(storage, doc, now) {
  const touch = readStoredLastTouch(storage, doc, now);
  if (!touch) return {};
  const out = {};
  UTM_KEYS.forEach((key) => {
    if (touch[key]) out[key] = touch[key];
  });
  if (touch.landing) out.landing_page = touch.landing;
  if (touch.referrer) out.referrer = touch.referrer;
  if (touch.pub) out.pub = touch.pub;
  if (typeof touch.ts === 'number' && Number.isFinite(touch.ts)) {
    out.last_touch_at = new Date(touch.ts).toISOString();
  }
  return out;
}

/**
 * The classified channel of the LAST touch - what the CRM stores as
 * `traffic_source`. Reads the `channel` word precomputed by buildTouch() at
 * capture time (which is how a later page view never needs a click id, live or
 * stored, to still report paid_search/paid_social), falling back to classifying
 * from the safely-stored utm_source/utm_medium/referrer alone.
 *
 * Returns '' - NOT 'direct' - when nothing is stored, so a caller can tell
 * "no ungated last touch at all" (fall through to your own logic) apart from
 * "a real touch that classified as direct". turboChannel() in
 * layout/theme.liquid depends on exactly that distinction.
 */
export function lastTouchChannel(storage, doc, now) {
  const touch = readStoredLastTouch(storage, doc, now);
  if (!touch) return '';
  if (touch.channel) return touch.channel;
  return classifyChannel({utm_medium: touch.utm_medium, utm_source: touch.utm_source, referrer: touch.referrer});
}

// ---- Session context (spec section 3, "Session context") ---------------------
//
// A SEPARATE storage key from turbo_first_touch. The first-touch snapshot is
// first-write-wins and must never be mutated after capture (its own tests assert
// a second page view leaves it byte-identical), so the per-visit counter cannot
// live inside it.
//
// STORAGE CONTRACT: `turbo_visits` holds only a visit counter and the epoch ms
// of the most recent page view. No click id, no url, no referrer, no utm value -
// so it is safe under the same ungated-capture reasoning as turbo_first_touch.

const VISITS_KEY = 'turbo_visits';

// GA's own session definition: 30 minutes of inactivity ends a session. Using an
// inactivity gap rather than sessionStorage keeps this a pure, testable function
// and keeps working in browsers that block sessionStorage but allow localStorage.
const SESSION_GAP_MS = 30 * 60 * 1000;

/**
 * `mobile | tablet | desktop`, from the user agent first and the viewport width
 * as the fallback. Tablet is checked before mobile because an Android tablet UA
 * contains "android" but not "mobile", and iPadOS reports "ipad".
 */
export function classifyDevice(userAgent, viewportWidth) {
  const ua = String(userAgent || '').toLowerCase();
  const width = typeof viewportWidth === 'number' && Number.isFinite(viewportWidth) ? viewportWidth : 0;
  if (/ipad|tablet|playbook|silk/.test(ua) || (/android/.test(ua) && !/mobile/.test(ua))) return 'tablet';
  if (/mobi|iphone|ipod|windows phone|iemobile|blackberry/.test(ua)) return 'mobile';
  if (width > 0 && width < 768) return 'mobile';
  if (width >= 768 && width < 1024) return 'tablet';
  return 'desktop';
}

/**
 * The stored `{count, last_at}` visit record, or null when nothing valid is
 * stored. Same localStorage-with-cookie-fallback convention as the first-touch
 * snapshot. Anything malformed reads as null rather than throwing.
 */
export function readVisitRecord(storage, doc) {
  try {
    const raw = (storage && storage.getItem(VISITS_KEY)) || readCookie(VISITS_KEY, doc);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const count = Number(parsed.count);
    const lastAt = Number(parsed.last_at);
    if (!Number.isFinite(count) || count < 1 || !Number.isFinite(lastAt)) return null;
    return {count: Math.floor(count), last_at: lastAt};
  } catch (e) {
    return null;
  }
}

/**
 * Called once per page view. Increments the visit counter only when more than
 * SESSION_GAP_MS has passed since the previous page view, then persists. A clock
 * that jumps backwards yields a non-positive delta, which is not greater than the
 * gap, so it can never inflate the count.
 */
export function recordVisit(storage, doc, now) {
  const at = typeof now === 'number' && Number.isFinite(now) ? now : Date.now();
  const existing = readVisitRecord(storage, doc);
  const record = existing
    ? {count: at - existing.last_at > SESSION_GAP_MS ? existing.count + 1 : existing.count, last_at: at}
    : {count: 1, last_at: at};
  const json = JSON.stringify(record);
  try {
    if (storage) storage.setItem(VISITS_KEY, json);
  } catch (e) {
    // storage may be full/blocked - the cookie fallback below still lands
  }
  writeCookie(VISITS_KEY, json, COOKIE_MAX_AGE_DAYS, doc);
  return record;
}

/**
 * The present-only session-context block turboAttrFields() spreads into every
 * lead payload: `device_type` always, `visit_count` and `last_touch_at` whenever
 * a visit record exists. `nav` is navigator-shaped ({userAgent}) and `win` is
 * window-shaped ({innerWidth}), both injectable so this stays testable in Node.
 */
export function sessionContext(storage, doc, nav, win) {
  const out = {};
  const record = readVisitRecord(storage, doc);
  if (record) {
    out.visit_count = record.count;
    out.last_touch_at = new Date(record.last_at).toISOString();
  }
  out.device_type = classifyDevice(nav && nav.userAgent, win && win.innerWidth);
  return out;
}

// ---- Browser bootstrap (no-op under Node/test - guarded on `window`) --------
if (typeof window !== 'undefined') {
  (function bootstrap() {
    function safeLocalStorage() {
      try {
        const probe = '__turbo_ft_probe__';
        window.localStorage.setItem(probe, '1');
        window.localStorage.removeItem(probe);
        return window.localStorage;
      } catch (e) {
        return null;
      }
    }

    // One probe for these three synchronous, same-page-load calls - safeLocalStorage()'s
    // result cannot change between them, so there is nothing to gain from probing again.
    // The exposed window.* functions below are invoked later (a form submit, a later
    // script), potentially long after this bootstrap runs, so each keeps its own probe
    // rather than closing over this one.
    const bootstrapStorage = safeLocalStorage();

    try {
      captureFirstTouchIfAbsent(bootstrapStorage, window.location, document);
    } catch (e) {
      // never let attribution break a page load
    }

    try {
      captureLastTouch(bootstrapStorage, window.location, document, window.location.hostname);
    } catch (e) {
      // never let attribution break a page load
    }

    try {
      recordVisit(bootstrapStorage, document, Date.now());
    } catch (e) {
      // never let attribution break a page load
    }

    window.turboFirstTouchFields = function () {
      try {
        return firstTouchFields(safeLocalStorage(), document);
      } catch (e) {
        return {};
      }
    };

    window.turboFirstTouchChannel = function () {
      try {
        return firstTouchChannel(safeLocalStorage(), document);
      } catch (e) {
        return 'direct';
      }
    };

    window.turboLastTouchFields = function () {
      try {
        return lastTouchFields(safeLocalStorage(), document);
      } catch (e) {
        return {};
      }
    };

    window.turboLastTouchChannel = function () {
      try {
        return lastTouchChannel(safeLocalStorage(), document);
      } catch (e) {
        return '';
      }
    };

    window.turboSessionContext = function () {
      try {
        return sessionContext(safeLocalStorage(), document, navigator, window);
      } catch (e) {
        return {};
      }
    };
  })();
}
